import { db, docFromSnapshot } from "../firebase";
import ability, { defineRulesFor } from "@/ability";
import {
  billingRequired,
  hasActiveSubscriptionAccess,
  isProtectedTenant,
  isSaasOwner,
  isSubscriptionExpired
} from "@/helpers/saasBilling";

const DEFAULT_SUBSCRIPTION = {
  endDate: null,
  id: null,
  plan: null,
  sessionId: null,
  stripeCustomer: null,
  stripeSubscription: null,
  trialPeriod: null
};

const state = {
  subscription: DEFAULT_SUBSCRIPTION,
  subscriptionLoaded: false
};

function billingIdentity(_state, getters, _rootState, rootGetters) {
  const claims = rootGetters.userClaims || {};
  return {
    uid: rootGetters.userId,
    storeId: claims.storeId,
    tenantId: rootGetters.tenantId,
    shop: rootGetters["shops/shop"]
  };
}

const getters = {
  subscription: state => state.subscription || DEFAULT_SUBSCRIPTION,
  subscriptionLoaded: state => state.subscriptionLoaded,
  subscriptionExists: state => !!(state.subscription && state.subscription.id),
  subscriptionIsTrialPeriod: state =>
    !!(state.subscription && state.subscription.trialPeriod),
  subscriptionIsBasic: state =>
    !!(state.subscription && state.subscription.plan === "BASIC"),
  subscriptionIsCancelled: state =>
    !!(state.subscription && state.subscription.cancelledDate),
  subscriptionIsExpired: (state, getters) =>
    !getters.isProtectedTenant && isSubscriptionExpired(state.subscription),
  billingIdentity,
  isProtectedTenant: (_state, getters) => isProtectedTenant(getters.billingIdentity),
  isSaasBilledTenant: (_state, getters) => isSaasOwner(getters.billingIdentity),
  hasPaidAccess: (state, getters) =>
    getters.isProtectedTenant ||
    hasActiveSubscriptionAccess(state.subscription),
  billingBlocked: (state, getters) =>
    billingRequired({
      ...getters.billingIdentity,
      subscription: state.subscription,
      subscriptionLoaded: state.subscriptionLoaded
    })
};

const actions = {
  async fetchSubscription(context) {
    const uid = context.getters.tenantId;
    if (!uid) {
      context.commit("setSubscription", DEFAULT_SUBSCRIPTION);
      context.commit("setSubscriptionLoaded", false);
      return;
    }
    const snapshot = await db
      .collection("subscriptions")
      .doc(uid)
      .get();
    const subscription = docFromSnapshot(snapshot) || {
      ...DEFAULT_SUBSCRIPTION,
      id: uid
    };
    const identity = billingIdentity(null, context.getters, null, context.rootGetters);
    const rulesForSubscription = defineRulesFor(subscription, {
      protectedTenant: isProtectedTenant(identity)
    });
    if (rulesForSubscription) {
      ability.update(rulesForSubscription);
    }
    context.commit("setSubscription", subscription);
    context.commit("setSubscriptionLoaded", true);
  }
};

const mutations = {
  setSubscription(state, subscription) {
    state.subscription = subscription || DEFAULT_SUBSCRIPTION;
  },
  setSubscriptionLoaded(state, loaded) {
    state.subscriptionLoaded = !!loaded;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
